import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildResourceSlotKeyPart } from '@go-shared/lib/reserve';
import { addDaysToIsoDate, dateToIsoUTC, formatTimeRange, isoLocal } from '@go-shared/lib/time';
import { mapBy } from '@go-shared/lib/functional';
import {
  ReservableEventTemplateSnapshot,
  ReservableEventWithTimeslots,
} from '@go-shared/types/reservable';
import { ReserveCacheTimeslot } from '@go-shared/types/reserveCache';
import { Time } from '@go-shared/types/time';
import { ObjectId } from 'bson';
import { FormikProps } from 'formik';
import React, { useMemo, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';
import { ReservationFormikForm } from './ReservationFormik';

// ─── internal types ──────────────────────────────────────────────────────────

interface SpotSlot {
  date: string;
  time: Time;
  duration?: number;
  resource?: string;
  host?: string;
  label?: string;
  spotsLeft: number;
  slotKey: string;
}

interface SpotSuggestion {
  id: string;
  occurrences: SpotSlot[];
  timeLabel: string;
}

function formatDateShort(isoDate: string): string {
  return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  });
}

function toSpotSlot(s: ReserveCacheTimeslot): SpotSlot {
  return {
    date: dateToIsoUTC(s.date),
    time: s.time,
    duration: s.duration,
    resource: s.resource,
    host: s.host,
    label: s.label,
    spotsLeft: s.spotsLeft,
    slotKey: s.slotKey,
  };
}

function slotTimeLabel(s: ReserveCacheTimeslot): string {
  const range = formatTimeRange(s.time, s.duration);
  return s.label ? `${s.label} (${range})` : range;
}

function generateSuggestions(
  timeslots: ReserveCacheTimeslot[],
  startDate: string,
  endDate: string,
  numDays: number,
  minSpots: number
): SpotSuggestion[] {
  const available = timeslots.filter(s => {
    const d = dateToIsoUTC(s.date);
    return d >= startDate && d <= endDate && s.spotsLeft >= minSpots;
  });

  if (!available.length || numDays < 1) return [];

  if (numDays === 1) {
    return available.slice(0, 10).map((s, i) => ({
      id: `sug-${i}`,
      occurrences: [toSpotSlot(s)],
      timeLabel: slotTimeLabel(s),
    }));
  }

  // Group by fingerprint: same resource + host + time + duration
  const groups: Record<string, ReserveCacheTimeslot[]> = {};
  for (const s of available) {
    const fp = buildResourceSlotKeyPart({ resource: s.resource, host: s.host }, s.time, s.duration);
    (groups[fp] ??= []).push(s);
  }

  const suggestions: SpotSuggestion[] = [];

  for (const slots of Object.values(groups)) {
    const dates = [...new Set(slots.map(s => dateToIsoUTC(s.date)))].sort();
    if (dates.length < numDays) continue;

    for (let i = 0; i <= dates.length - numDays; i++) {
      if (suggestions.length >= 10) break;
      const window = dates.slice(i, i + numDays);
      const occurrences = window.map(date => {
        const s = slots.find(sl => dateToIsoUTC(sl.date) === date)!;
        return toSpotSlot(s);
      });
      const first = slots.find(sl => dateToIsoUTC(sl.date) === window[0])!;
      suggestions.push({
        id: `sug-${suggestions.length}`,
        occurrences,
        timeLabel: slotTimeLabel(first),
      });
    }

    if (suggestions.length >= 10) break;
  }

  return suggestions;
}

// ─── component ───────────────────────────────────────────────────────────────

export interface FindASpotFillerProps {
  formik: FormikProps<ReservationFormikForm>;
  eventSnapshot: ReservableEventTemplateSnapshot;
  reservableDetails?: ReservableEventWithTimeslots;
  onClose: () => void;
}

export default function FindASpotFiller({
  formik,
  eventSnapshot,
  reservableDetails,
  onClose,
}: FindASpotFillerProps) {
  const today = isoLocal();

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDaysToIsoDate(today, 30));
  const [numDays, setNumDays] = useState(2);

  const ticketsMap = mapBy(eventSnapshot.reservables.tickets, t => t._id);
  const minSpots = Math.max(1, formik.values.tickets.length);

  const suggestions = useMemo(() => {
    if (!reservableDetails?.timeslots || !startDate || !endDate) return [];
    return generateSuggestions(reservableDetails.timeslots, startDate, endDate, numDays, minSpots);
  }, [reservableDetails?.timeslots, startDate, endDate, numDays, minSpots]);

  function applyOccurrences(occurrences: SpotSlot[]) {
    if (!occurrences.length) return;
    const [main, ...rest] = [...occurrences].sort((a, b) => a.date.localeCompare(b.date));
    formik.setValues({
      ...formik.values,
      date: main.date,
      time: main.time,
      duration: main.duration,
      resource: main.resource,
      host: main.host,
      additionalOccurrences: rest.map(occ => ({
        _id: new ObjectId().toString(),
        date: occ.date,
        time: occ.time,
        duration: occ.duration,
        resource: occ.resource,
        host: occ.host,
      })),
    });
    onClose();
  }

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="grid grid-cols-3 gap-4">
        <BubbleFormGroup label="Start Date">
          <BubbleInput
            block
            type="date"
            value={startDate}
            min={today}
            onChange={e => setStartDate(e.target.value)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="End Date">
          <BubbleInput
            block
            type="date"
            value={endDate}
            min={startDate || today}
            onChange={e => setEndDate(e.target.value)}
          />
        </BubbleFormGroup>
        <BubbleFormGroup label="Number of Days">
          <BubbleInput
            block
            type="number"
            value={numDays}
            min={1}
            max={31}
            onChange={e => setNumDays(Math.max(1, parseInt(e.target.value) || 1))}
          />
        </BubbleFormGroup>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className="text-sm font-semibold text-navy">
          {suggestions.length > 0
            ? `${suggestions.length} option${suggestions.length !== 1 ? 's' : ''} found`
            : 'No options found'}
        </div>
        {suggestions.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {suggestions.map(sug => (
              <BubbleButton
                key={sug.id}
                variant="navy"
                inverse
                className="border border-navy"
                onClick={() => applyOccurrences(sug.occurrences)}
              >
                <div className="font-semibold text-sm text-navy mb-1 truncate">{sug.timeLabel}</div>
                <div className="text-xs text-slate space-y-0.5">
                  {sug.occurrences.map(occ => {
                    const rental = occ.resource ? ticketsMap[occ.resource]?.name : undefined;
                    return (
                      <div key={occ.date} className="flex items-center gap-1">
                        <FontAwesomeIcon icon="calendar" className="text-turquoise shrink-0" />
                        <span className="truncate">
                          {formatDateShort(occ.date)}
                          {rental ? ` · ${rental}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </BubbleButton>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate italic">
            Try adjusting the date range or number of days.
          </div>
        )}
      </div>
    </div>
  );
}
